import { Circle, Image, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { withTranslation } from 'react-i18next';

import commonStyles from 'views/SpecificationDocument/DocumentView/Common/commonStyles';

import Point from './Point';
import PointDrawing from './PointDrawing';

// The interactive comment editor (layout/molecules/Playground/Playground.tsx) stores
// x/y/noteX/noteY as raw pixels on a fixed 750x650 canvas. All point coordinates must
// be scaled from that canvas into this document's image box.
const EDITOR_WIDTH = 750;
const EDITOR_HEIGHT = 650;

const COLUMN_GAP = 20;

const IMAGE_PADDING_RATIO = 35 / 525;

const IMAGE_WIDTH = 547;
const IMAGE_HEIGHT = 474;

const MARKER_RADIUS = 4;

const styles = StyleSheet.create({
  projectionContainer: {
    marginHorizontal: commonStyles.marginHorizontal,
  },
  projectionTitleWrapper: {
    flexDirection: 'row',
    ...commonStyles.fontM,
  },
  projectionTitle: {
    fontWeight: 'bold',
  },
  titleImageGroup: {
    alignItems: 'flex-start',
  },
  imageWrapper: {
    position: 'relative',
  },
  bubbleColumn: {
    flexDirection: 'column',
    marginHorizontal: commonStyles.marginHorizontal,
  },
  firstBubbleColumn: {
    marginTop: COLUMN_GAP,
  },
  svgOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
});

function parsePoints(points, type) {
  return points.filter((point) => point.typeCode === type);
}

function scalePoint(point, imageWidth, imageHeight) {
  return {
    x: (point.x * imageWidth) / EDITOR_WIDTH,
    y: (point.y * imageHeight) / EDITOR_HEIGHT,
  };
}

// eslint-disable-next-line arrow-body-style
function Projection({ projection, type, t }) {
  const points = parsePoints(projection.points, type).sort((a, b) => a.y - b.y);
  const hasPoints = points.length > 0;

  const imageWidth = IMAGE_WIDTH;
  const imageHeight = IMAGE_HEIGHT;
  const imagePadding = imageWidth * IMAGE_PADDING_RATIO;

  return (
    <>
      <View style={styles.projectionContainer}>
        <View style={styles.titleImageGroup} wrap={false}>
          <View style={styles.projectionTitleWrapper}>
            <Text>{t('projection')}:</Text>
            <Text style={styles.projectionTitle}>{`${projection.name} ${t(
              type === 'DESIGNER'
                ? 'designer_comments_with_addon'
                : 'technologist_comments_with_addon',
            )}
          `}</Text>
          </View>
          <View style={[styles.imageWrapper, { width: imageWidth, height: imageHeight }]}>
            <Image
              style={{
                padding: imagePadding,
                width: imageWidth,
                height: imageHeight,
                objectFit: 'contain',
              }}
              src={projection.picture}
            />
            {hasPoints && (
              <Svg
                style={[styles.svgOverlay, { width: imageWidth, height: imageHeight }]}
                viewBox={`0 0 ${imageWidth} ${imageHeight}`}
                preserveAspectRatio="none"
              >
                {points.map((point, orderIndex) => {
                  const pin = scalePoint(point, imageWidth, imageHeight);

                  return (
                    <React.Fragment key={point.id}>
                      <Circle cx={pin.x} cy={pin.y} r={MARKER_RADIUS} fill="#000" />
                      <Text x={pin.x} y={pin.y + 2.2} fill="#fff" fontSize={6} textAnchor="middle">
                        {orderIndex + 1}
                      </Text>
                    </React.Fragment>
                  );
                })}
              </Svg>
            )}
          </View>
        </View>
      </View>
      {hasPoints &&
        points.map((point, orderIndex) => (
          <React.Fragment key={point.id}>
            <View
              style={[
                styles.bubbleColumn,
                { width: imageWidth },
                orderIndex === 0 && styles.firstBubbleColumn,
              ]}
            >
              <Point
                point={point}
                orderIndex={orderIndex}
                projectionIdx={projection.number}
                width={imageWidth}
              />
            </View>
            {point.photo && (
              <PointDrawing point={point} pointIndex={orderIndex} projection={projection} />
            )}
          </React.Fragment>
        ))}
    </>
  );
}

export default withTranslation('specification')(Projection);
